import { useState } from 'react';
import { API_BASE } from '../lib/config';

// --- Reminders (web push) ------------------------------------------------------
export type RemindState = 'idle' | 'working' | 'on' | 'denied' | 'unsupported' | 'install' | 'off-server' | 'error';
const PREFS_KEY = 'zuzu_chat_hour_reminder';
const b64ToBytes = (b64: string) => {
  const s = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
};

// Chat Hour reminders (web push). Used on the Chat Hour page and after a Meet Again.
export function useReminders() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const ios = typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent);
  const standalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || 'null'); } catch { return null; } })() as { slots: number[]; lead: number } | null;
  const [state, setState] = useState<RemindState>(() => (!supported ? (ios && !standalone ? 'install' : 'unsupported') : saved ? 'on' : 'idle'));
  const [prefs, setPrefs] = useState<{ slots: number[]; lead: number }>(saved ?? { slots: [0, 1], lead: 10 });
  const [detail, setDetail] = useState('');

  const turnOn = async (next = prefs) => {
    if (!next.slots.length) return;
    setState('working');
    setDetail('');
    try {
      const keyRes = await fetch(`${API_BASE}/api/push/key`);
      if (!keyRes.ok) { setState('off-server'); return; }
      const { key } = await keyRes.json();
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState('denied'); return; }
      const appKey = b64ToBytes(key);
      const reg = await navigator.serviceWorker.ready;
      // An older reminder made with another key can't be reused: replace it.
      let sub = await reg.pushManager.getSubscription();
      const old = sub?.options?.applicationServerKey;
      if (sub && old && !new Uint8Array(old).every((b, i) => b === appKey[i])) { await sub.unsubscribe(); sub = null; }
      if (!sub) {
        try {
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
        } catch (e) {
          // One retry after clearing anything left over.
          await (await reg.pushManager.getSubscription())?.unsubscribe();
          try { sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey }); }
          catch (e2) { setDetail(String((e2 as Error)?.message || e2 || e)); setState('error'); return; }
        }
      }
      const r = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON(), slots: next.slots, lead: next.lead }),
      });
      if (!r.ok) { setDetail((await r.json().catch(() => ({}))).error || `Server said ${r.status}`); setState('error'); return; }
      try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      setPrefs(next);
      setState('on');
    } catch (e) {
      setDetail(String((e as Error)?.message || e));
      setState('error');
    }
  };
  const turnOff = async () => {
    setState('working');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch(`${API_BASE}/api/push/unsubscribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint }) });
        await sub.unsubscribe();
      }
    } catch { /* already gone */ }
    try { localStorage.removeItem(PREFS_KEY); } catch { /* private mode */ }
    setState('idle');
  };
  return { state, prefs, setPrefs, turnOn, turnOff, detail };
}

